import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowRight, Check, PhoneCall, MapPin, FileText } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import CourtPreview from "../components/CourtPreview";
import { ContactFields, EMPTY_CONTACT, validateContact, trimContact } from "../components/ContactFields";
import { CONVERTED } from "../lib/gtag";
import Seo from "../components/Seo";
import { PHONE_DISPLAY, PHONE_TEL, EMAIL } from "../data/contact";

const NEXT = [
  { Icon: PhoneCall, t: "We call you", d: "A Speedy specialist rings your mobile — usually within one business day." },
  { Icon: MapPin, t: "Free site visit", d: "We measure the court, check the base and talk through colours and lines." },
  { Icon: FileText, t: "Itemised quote", d: "A clear, obligation-free proposal lands in your inbox." },
];

export default function Quote() {
  const nav = useNavigate();
  const { state } = useLocation();
  const preset = state?.config;
  const sport = preset?.sport || state?.sport || null;
  const source = state?.source || null;
  const [form, setForm] = useState(EMPTY_CONTACT);
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const err = validateContact(form);
    if (err) return toast.error(err);
    setSending(true);
    try {
      await api.post("/leads", {
        type: "quote",
        ...trimContact(form),
        court_type: sport,
        data: {
          ...(preset ? { colours: { base: preset.base, key: preset.key, border: preset.border, line: preset.line }, from_customiser: true } : {}),
          ...(source ? { source_article: source } : {}),
        },
      });
      toast.success("Quote request submitted!");
      nav("/thank-you", CONVERTED("quote"));
    } catch {
      toast.error(`Something went wrong. Please try again or call ${PHONE_DISPLAY}.`);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="pt-28 pb-28 min-h-screen noise">
      <Seo title="Get a Free Court Resurfacing Quote | Speedy Resurfacing Sydney"
           description="Request a free, no-obligation quote for court resurfacing, custom design or line marking anywhere in Sydney and NSW. Just your name, mobile and email."
           path="/quote" />
      <div className="mx-auto max-w-3xl px-5 md:px-10">
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-neon">Obligation-free · Sydney &amp; NSW</span>
        <h1 className="mt-4 font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter">REQUEST A QUOTE</h1>
        <p className="mt-4 max-w-xl text-zinc-300">
          Three details — that's it. We call you, visit the court and come back with an itemised quote.
        </p>

        <form onSubmit={submit} data-testid="quote-form" className="mt-10 rounded-lg border border-white/10 bg-obsidian-800 p-6 md:p-8">
          {preset && (
            <div className="mb-6 flex items-center gap-4 rounded-lg bg-obsidian-700 p-4" data-testid="quote-preset">
              <CourtPreview base={preset.base} keyColor={preset.key} border={preset.border} line={preset.line} flat className="w-28 shrink-0" />
              <div>
                <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Your design</span>
                <p className="text-sm text-zinc-200">{preset.sport || "Custom court"} — colours attached to this quote.</p>
              </div>
            </div>
          )}
          {!preset && sport && (
            <div className="mb-6 flex items-center gap-3 rounded-lg bg-obsidian-700 p-4" data-testid="quote-sport">
              <span className="rounded-sm bg-gold px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest text-obsidian">{sport}</span>
              <p className="text-sm text-zinc-200">Enquiring about {sport.toLowerCase()} court resurfacing — we'll tailor the site visit accordingly.</p>
            </div>
          )}
          <ContactFields form={form} set={set} testid="quote" />
          <button type="submit" disabled={sending} data-testid="quote-submit"
                  className="group mt-7 inline-flex w-full items-center justify-between gap-3 rounded-sm bg-neon px-7 py-4 font-heading font-black text-lg text-obsidian shadow-neon hover:bg-neon-hover disabled:opacity-50 transition-colors md:w-auto">
            {sending ? "Sending…" : "Submit Request"}
            {sending ? <ArrowRight size={20} /> : <Check size={20} className="group-hover:translate-x-1 transition-transform" />}
          </button>
          <p className="mt-4 font-mono text-[10px] text-zinc-600">
            Or call <a href={PHONE_TEL} className="hover:text-gold">{PHONE_DISPLAY}</a> · {EMAIL}
          </p>
        </form>

        <div className="mt-8 grid gap-3 sm:grid-cols-3" data-testid="quote-next-steps">
          {NEXT.map(({ Icon, t, d }, i) => (
            <div key={t} className="rounded-lg border border-white/10 bg-obsidian-800 p-5">
              <span className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-gold">
                <Icon size={13} /> 0{i + 1}
              </span>
              <p className="mt-2 font-heading font-bold">{t}</p>
              <p className="mt-1 text-xs text-zinc-400">{d}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
